/**
 * SEO Helper Functions for Botaniksan E-commerce
 * Provides utilities for generating SEO-optimized content
 */

// Generate SEO-friendly alt text for images
export const generateAltText = (context, item, fallback = "") => {
  switch (context) {
    case "product":
      return item && item.name
        ? `${item.name} - Botaniksan'da satın al`
        : fallback || "Botaniksan ürün görseli";

    case "category":
      return item && item.title
        ? `${item.title} kategorisi - Botaniksan`
        : fallback || "Botaniksan kategori görseli";

    case "slider":
      return item && item.title
        ? `${item.title} - Botaniksan`
        : item && item.description
        ? `${item.description} - Botaniksan`
        : fallback || "Botaniksan öne çıkan görsel";

    case "logo":
      return "Botaniksan - Doğal Ürünler ve Bitkisel Çözümler";

    case "icon":
      return item && item.name
        ? `${item.name} ikonu`
        : fallback || "Botaniksan ikon";

    default:
      return fallback || "Botaniksan";
  }
};

// Generate page titles with SEO best practices
export const generatePageTitle = (pageType, item, siteName = "Botaniksan") => {
  const maxLength = 60; // Google's recommended title length

  switch (pageType) {
    case "home":
      return `${siteName} - Doğal Ürünler ve Bitkisel Çözümler`;

    case "category":
      const categoryTitle = `${
        (item && item.title) || "Kategori"
      } - ${siteName}`;
      return categoryTitle.length > maxLength
        ? `${(item && item.title) || "Kategori"} | ${siteName}`
        : categoryTitle;

    case "product":
      const productTitle = `${(item && item.name) || "Ürün"} - ${siteName}`;
      return productTitle.length > maxLength
        ? `${(item && item.name) || "Ürün"} | ${siteName}`
        : productTitle;

    case "page":
      const pageTitle = `${(item && item.title) || "Sayfa"} - ${siteName}`;
      return pageTitle.length > maxLength
        ? `${(item && item.title) || "Sayfa"} | ${siteName}`
        : pageTitle;

    case "checkout":
      return `Sepet ve Ödeme - ${siteName}`;

    case "account":
      return `Hesabım - ${siteName}`;

    case "login":
      return `Giriş Yap - ${siteName}`;

    case "register":
      return `Üye Ol - ${siteName}`;

    default:
      return `${siteName} - Doğal Ürünler`;
  }
};

// Generate meta descriptions with SEO best practices
export const generateMetaDescription = (
  pageType,
  item,
  siteName = "Botaniksan"
) => {
  const maxLength = 160; // Google's recommended description length

  let description = "";

  switch (pageType) {
    case "home":
      description = `${siteName}'da doğal ve organik ürünler, bitkisel çözümler ve sağlıklı yaşam ürünleri. Kaliteli ve güvenilir doğal ürünler için doğru adres.`;
      break;

    case "category":
      description = `${
        (item && item.title) || "Kategori"
      } ürünleri ${siteName}'da. Doğal ve kaliteli ${
        (item && item.title && item.title.toLowerCase()) || "ürünler"
      } için hemen göz atın.`;
      break;

    case "product":
      if (item && item.meta_description) {
        description = item.meta_description;
      } else if (item && item.content) {
        // Extract clean text from HTML content
        const cleanContent = item.content
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        description =
          cleanContent.length > maxLength
            ? `${cleanContent.substring(0, maxLength - 20)}...`
            : cleanContent;
      } else {
        const price = (item && item.sale_price) || (item && item.price);
        description = `${(item && item.name) || "Ürün"} ${
          price ? `₺${price} fiyatıyla ` : ""
        }${siteName}'da. Doğal ve kaliteli ürünler için hemen sipariş verin.`;
      }
      break;

    case "page":
      if (item && item.meta_description) {
        description = item.meta_description;
      } else if (item && item.content) {
        const cleanContent = item.content
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        description =
          cleanContent.length > maxLength
            ? `${cleanContent.substring(0, maxLength - 20)}...`
            : cleanContent;
      } else {
        description = `${(item && item.title) || "Sayfa"} - ${siteName}`;
      }
      break;

    case "checkout":
      description = `Sepetinizi gözden geçirin ve güvenli ödeme seçenekleriyle siparişinizi tamamlayın. ${siteName}'da güvenli alışveriş.`;
      break;

    case "account":
      description = `${siteName} hesap yönetimi. Siparişlerinizi takip edin, adres bilgilerinizi güncelleyin ve hesap ayarlarınızı yönetin.`;
      break;

    case "login":
      description = `${siteName} üye girişi. Hesabınıza giriş yaparak özel fırsatları kaçırmayın.`;
      break;

    case "register":
      description = `${siteName}'a üye olun ve özel indirimlerden faydalanın. Ücretsiz üyelik ile avantajlı alışverişin tadını çıkarın.`;
      break;

    default:
      description = `${siteName} - Doğal ve organik ürünler için güvenilir adres.`;
  }

  // Ensure description doesn't exceed max length
  return description.length > maxLength
    ? `${description.substring(0, maxLength - 3)}...`
    : description;
};

// Generate keywords for pages
export const generateKeywords = (pageType, item, baseKeywords = []) => {
  const defaultKeywords = [
    "botaniksan",
    "doğal ürünler",
    "organik",
    "bitkisel",
    "sağlıklı yaşam",
  ];

  let keywords = [...defaultKeywords, ...baseKeywords];

  switch (pageType) {
    case "category":
      if (item && item.title) {
        keywords.push(
          item.title.toLowerCase(),
          `${item.title.toLowerCase()} ürünleri`
        );
      }
      break;

    case "product":
      if (item && item.name) {
        keywords.push(item.name.toLowerCase());
      }
      if (item && item.categories) {
        item.categories.forEach((cat) => {
          keywords.push(cat.title.toLowerCase());
        });
      }
      break;

    case "page":
      if (item && item.title) {
        keywords.push(item.title.toLowerCase());
      }
      break;
  }

  // Remove duplicates and return as comma-separated string
  return [...new Set(keywords)].join(", ");
};

// Generate breadcrumb data for structured markup
export const generateBreadcrumbs = (pageType, item, location) => {
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? "http://localhost:3000"
      : "https://botaniksan.com";

  const breadcrumbs = [{ name: "Ana Sayfa", url: baseUrl }];

  switch (pageType) {
    case "category":
      breadcrumbs.push({
        name: (item && item.title) || "Kategori",
        url: `${baseUrl}/kategori/${item && item.slug}`,
      });
      break;

    case "product":
      if (item && item.categories && item.categories.length > 0) {
        const category = item.categories[0];
        breadcrumbs.push({
          name: category.title,
          url: `${baseUrl}/kategori/${category.slug}`,
        });
      }
      breadcrumbs.push({
        name: (item && item.name) || "Ürün",
        url: `${baseUrl}/urun/${item && item.slug}`,
      });
      break;

    case "page":
      breadcrumbs.push({
        name: (item && item.title) || "Sayfa",
        url: `${baseUrl}/p/${item && item.slug}`,
      });
      break;

    case "checkout":
      breadcrumbs.push({
        name: "Sepet",
        url: `${baseUrl}/sepet`,
      });
      breadcrumbs.push({
        name: "Ödeme",
        url: `${baseUrl}/odeme`,
      });
      break;

    case "account":
      breadcrumbs.push({
        name: "Hesabım",
        url: `${baseUrl}/hesabim`,
      });
      break;
  }

  return breadcrumbs;
};

// Check if content should be indexed
export const shouldIndex = (pageType, item) => {
  // Don't index certain pages
  const noIndexPages = ["checkout", "login", "register", "account"];

  if (noIndexPages.includes(pageType)) {
    return false;
  }

  // Don't index products that are out of stock (optional)
  if (pageType === "product" && item && item.stock === 0) {
    return false; // You can change this to true if you want to index out of stock products
  }

  return true;
};

// Generate canonical URL
export const generateCanonicalUrl = (pageType, item) => {
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? "http://localhost:3000"
      : "https://botaniksan.com";

  switch (pageType) {
    case "home":
      return baseUrl;

    case "category":
      return `${baseUrl}/kategori/${item && item.slug}`;

    case "product":
      return `${baseUrl}/urun/${item && item.slug}`;

    case "page":
      return `${baseUrl}/p/${item && item.slug}`;

    default:
      return `${baseUrl}${window.location.pathname}`;
  }
};

// Image optimization helpers
export const optimizeImageForSEO = (imageUrl, context, item) => {
  if (!imageUrl) return null;

  const alt = generateAltText(context, item);
  const title =
    context === "product"
      ? (item && item.name)
      : context === "category"
      ? (item && item.title)
      : alt;

  return {
    src: imageUrl,
    alt,
    title,
    loading: "lazy", // Native lazy loading for modern browsers
  };
};

export default {
  generateAltText,
  generatePageTitle,
  generateMetaDescription,
  generateKeywords,
  generateBreadcrumbs,
  shouldIndex,
  generateCanonicalUrl,
  optimizeImageForSEO,
};



// WEBPACK FOOTER //
// ./src/helpers/seo.js